import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyRupeeIcon,
  TagIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  TruckIcon,
  DocumentTextIcon,
  CreditCardIcon,
  GiftIcon,
  QrCodeIcon,
  BuildingOfficeIcon,
  IdentificationIcon,
  PencilIcon,
  PlusIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { getFirestore, doc, getDoc, updateDoc, addDoc, collection } from 'firebase/firestore';

const MultiUsePassDetail = () => {
  const { passId } = useParams();
  const navigate = useNavigate();
  const [pass, setPass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    passname: '',
    passnumber: '',
    state: '',
    city: '',
    address: '',
    passcode: '',
    qrcode: '',
    status: '',
    multiple: false,
    duration: '',
    durationUnit: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Determine the pass type and appropriate title based on the current URL
  const getPassTypeAndTitle = () => {
    const pathname = window.location.pathname;
    if (pathname.includes('partner-deal-coupon-detail')) {
      return { type: 'partner-deal-coupon', title: 'Partner Discount Coupon Details' };
    } else if (pathname.includes('perks-deal-voucher-detail')) {
      return { type: 'perks-deal-voucher', title: 'Perks Deal Voucher Details' };
    } else if (pathname.includes('one-time-pass-detail')) {
      return { type: 'one-time-pass', title: 'One-time Pass Details' };
    } else {
      return { type: 'multi-use-pass', title: 'Multi-use Pass Details' };
    }
  };
  
  const { type: passType, title: pageTitle } = getPassTypeAndTitle();

  useEffect(() => {
    loadPassDetail();
  }, [passId]);

  useEffect(() => {
    if (pass) {
      setEditForm({
        passname: pass.passname || '',
        passnumber: pass.passnumber || '',
        state: pass.state || '',
        city: pass.city || '',
        address: pass.address || '',
        passcode: pass.passcode || '',
        qrcode: pass.qrcode || '',
        status: pass.status || '',
        multiple: pass.multiple || false,
        duration: pass.duration || '',
        durationUnit: pass.durationUnit || ''
      });
    }
  }, [pass]);

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };



  const handleSave = async () => {
    try {
      setSaving(true);
      const db = getFirestore();
      const passRef = doc(db, 'pass', passId);
      
      await updateDoc(passRef, {
        ...editForm,
        updateTime: new Date()
      });
      
      // Reload the pass data
      await loadPassDetail();
      setIsEditing(false);
    } catch (err) {
      setError('Failed to update pass');
      console.error('Error updating pass:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    try {
      setSaving(true);
      const db = getFirestore();
      const passRef = doc(db, 'pass', passId);
      
      await updateDoc(passRef, {
        status: 'active',
        updateTime: new Date()
      });
      
      await loadPassDetail();
    } catch (err) {
      setError('Failed to activate pass');
      console.error('Error activating pass:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      setSaving(true);
      const db = getFirestore();
      const passRef = doc(db, 'pass', passId);
      
      await updateDoc(passRef, {
        status: 'inactive',
        updateTime: new Date()
      });
      
      await loadPassDetail();
    } catch (err) {
      setError('Failed to deactivate pass');
      console.error('Error deactivating pass:', err);
    } finally {
      setSaving(false);
    }
  };



  const loadPassDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const db = getFirestore();
      const passRef = doc(db, 'pass', passId);
      const passSnapshot = await getDoc(passRef);
      
      if (passSnapshot.exists()) {
        setPass({
          id: passSnapshot.id,
          ...passSnapshot.data()
        });
      } else {
        setError('Pass not found');
      }
    } catch (err) {
      setError('Failed to load pass details');
      console.error('Error loading pass detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      let date;
      if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        // Firestore Timestamp object
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp && timestamp.toDate) {
        // Firestore Timestamp with toDate method
        date = timestamp.toDate();
      } else {
        // Regular date string or number
        date = new Date(timestamp);
      }
      return format(date, 'MMM dd, yyyy HH:mm');
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00';
    return `₹${parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const getStatusColor = (status) => {
    if (!status || typeof status !== 'string') {
      return 'bg-gray-100 text-gray-800';
    }
    
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'used':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderField = (label, value, icon = null) => {
    // Safely convert value to string
    let displayValue = 'N/A';
    if (value !== null && value !== undefined) {
      if (typeof value === 'object') {
        // Handle Firestore Timestamp objects
        if (value.seconds) {
          displayValue = formatDate(value);
        } else {
          // For other objects, try to stringify or show a placeholder
          try {
            displayValue = JSON.stringify(value);
          } catch {
            displayValue = '[Object]';
          }
        }
      } else {
        displayValue = String(value);
      }
    }

    return (
      <div>
        <label className="block text-sm font-medium text-gray-500">{label}</label>
        <p className="mt-1 text-sm text-gray-900 flex items-center">
          {icon && <span className="mr-2">{icon}</span>}
          {displayValue}
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <XCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={() => navigate('/multi-use-passes')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Back to List
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!pass) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Pass Not Found</h2>
              <p className="text-gray-600 mb-4">The requested pass could not be found.</p>
              <button
                onClick={() => navigate('/multi-use-passes')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Back to List
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  if (passType === 'partner-deal-coupon') {
                    navigate('/partner-deal-coupons');
                  } else if (passType === 'perks-deal-voucher') {
                    navigate('/perks-deal-vouchers');
                  } else if (passType === 'one-time-pass') {
                    navigate('/one-time-passes');
                  } else {
                    navigate('/multi-use-passes');
                  }
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              <h1 className="text-2xl font-semibold text-gray-900">{pageTitle}</h1>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(pass.status)}`}>
                {pass.status || 'Unknown'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Pass Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <TagIcon className="h-5 w-4 mr-2 text-blue-600" />
                  Basic Pass Information
                </h2>
                {(passType === 'multi-use-pass' || passType === 'one-time-pass') && (
                  <div className="flex space-x-2">
                    {isEditing ? (
                      <button
                        onClick={() => setIsEditing(false)}
                        className="flex items-center px-3 py-1 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                      >
                        <XCircleIcon className="h-4 w-4 mr-1" />
                        Cancel
                      </button>
                    ) : (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Pass Name</label>
                    <input
                      type="text"
                      value={editForm.passname}
                      onChange={(e) => handleEditFormChange('passname', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Pass Number (Owner Mobile)</label>
                    <input
                      type="text"
                      value={editForm.passnumber}
                      onChange={(e) => handleEditFormChange('passnumber', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Pass State</label>
                    <input
                      type="text"
                      value={editForm.state}
                      onChange={(e) => handleEditFormChange('state', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Pass City</label>
                    <input
                      type="text"
                      value={editForm.city}
                      onChange={(e) => handleEditFormChange('city', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-500">Pass Address</label>
                    <input
                      type="text"
                      value={editForm.address}
                      onChange={(e) => handleEditFormChange('address', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Pass Code</label>
                    <input
                      type="text"
                      value={editForm.passcode}
                      onChange={(e) => handleEditFormChange('passcode', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Pass QR</label>
                    <input
                      type="text"
                      value={editForm.qrcode}
                      onChange={(e) => handleEditFormChange('qrcode', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Pass Status</label>
                    <select
                      value={editForm.status}
                      onChange={(e) => handleEditFormChange('status', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Status</option>
                      <option value="active">Enable</option>
                      <option value="inactive">Disable</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Pass Use Type</label>
                    <select
                      value={editForm.multiple ? 'Multiple' : 'One Time'}
                      onChange={(e) => handleEditFormChange('multiple', e.target.value === 'Multiple')}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Type</option>
                      <option value="Multiple">Multiple</option>
                      <option value="One Time">One Time</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Pass Duration</label>
                    <input
                      type="number"
                      value={editForm.duration}
                      onChange={(e) => handleEditFormChange('duration', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Duration Unit</label>
                    <select
                      value={editForm.durationUnit}
                      onChange={(e) => handleEditFormChange('durationUnit', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Unit</option>
                      <option value="days">Days</option>
                      <option value="weeks">Weeks</option>
                      <option value="months">Months</option>
                      <option value="years">Years</option>
                    </select>
                  </div>
                  <div className="md:col-span-2 flex justify-end space-x-3 mt-4">
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <CheckIcon className="h-4 w-4 mr-1" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderField('Pass Code', pass.passcode, <DocumentTextIcon className="h-4 w-4 text-gray-400" />)}
                  {renderField('Pass Name', pass.passname)}
                  {renderField('Pass Number', pass.passnumber)}
                  {renderField('Pass Amount', formatCurrency(pass.passamount))}
                  {renderField('Pass Type', pass.type)}
                  {renderField('Status', pass.status)}
                  {renderField('Multiple', pass.multiple ? 'Yes' : 'No')}
                </div>
              )}
            </div>



            {/* Purchase & Payment Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CreditCardIcon className="h-5 w-5 mr-2 text-green-600" />
                Purchase & Payment Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderField('Purchase Date', pass.PurchaseDate, <CalendarIcon className="h-4 w-4 text-gray-400" />)}
                {renderField('Payment By', pass.paymentby)}
                {renderField('Deal Discounted Price', formatCurrency(pass.dealDiscountedPrice))}
                {renderField('Deal Provider Name', pass.dealProviderName)}
                {renderField('Deal Status', pass.dealstatus)}
                {renderField('Deal Rating', pass.dealsrating)}
              </div>
            </div>

            {/* Deal Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <TagIcon className="h-5 w-5 mr-2 text-purple-600" />
                Deal Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderField('Deal Title', pass.dealtitle)}
                {renderField('Deal Expiry', pass.dealexpiry)}
                {renderField('Deals Valid Date', pass.dealsValidDate)}
                {renderField('Coupon Listing ID', pass.couponListingId)}
                {renderField('Duration', pass.duration)}
                {renderField('Duration Unit', pass.durationUnit)}
              </div>
            </div>

            {/* Gift Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <GiftIcon className="h-5 w-5 mr-2 text-pink-600" />
                Gift Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderField('Deal Gift', pass.dealgift ? 'Yes' : 'No')}
                {renderField('Deal Gift Name', pass.dealgiftname)}
                {renderField('Deal Gift Email', pass.dealgiftemail, <EnvelopeIcon className="h-4 w-4 text-gray-400" />)}
                {renderField('Deal Gift Phone', pass.dealgiftphone, <PhoneIcon className="h-4 w-4 text-gray-400" />)}
                {renderField('Deal Gift Sent', pass.dealgiftsent ? 'Yes' : 'No')}
                {renderField('Gifted User ID', pass.gifteduserid)}
              </div>
            </div>

            {/* Pass Issued Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <UserIcon className="h-5 w-5 mr-2 text-indigo-600" />
                Pass Issued Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderField('Pass Issued To Name', pass.passissuedtoname)}
                {renderField('Pass Issued To Email', pass.passissuedtoemail, <EnvelopeIcon className="h-4 w-4 text-gray-400" />)}
                {renderField('Pass Issued To Number', pass.passissuedtonumber, <PhoneIcon className="h-4 w-4 text-gray-400" />)}
                {renderField('Issued User ID', pass.issueduserid)}
                {renderField('User IDs', pass.userids)}
              </div>
            </div>

            {/* Vehicle Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <TruckIcon className="h-5 w-5 mr-2 text-orange-600" />
                Vehicle Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderField('Pass Vehicle Number', pass.passvehiclenumber)}
                {renderField('Pass Vehicle Type', pass.passvehicletype)}
              </div>
            </div>

            {/* Visitor Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <IdentificationIcon className="h-5 w-5 mr-2 text-teal-600" />
                Visitor Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderField('Visitor First Name', pass.visitorfirstname)}
                {renderField('Visitor Last Name', pass.visitorlastname)}
                {renderField('Attendant Name', pass.attendantname)}
                {renderField('Purpose of Visit', pass.purposeofvisit)}
                {renderField('Time of Visit', pass.timeofvisit, <ClockIcon className="h-4 w-4 text-gray-400" />)}
              </div>
            </div>

            {/* Location Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <MapPinIcon className="h-5 w-5 mr-2 text-red-600" />
                Location Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderField('Address', pass.address)}
                {renderField('City', pass.city)}
                {renderField('State', pass.state)}
                {renderField('Flat Number', pass.flatnumber)}
              </div>
            </div>

            {/* QR Code & Technical Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <QrCodeIcon className="h-5 w-5 mr-2 text-gray-600" />
                Technical Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderField('QR Code', pass.qrcode)}
                {renderField('Created', pass.created)}
                {renderField('Building Office', pass.buildingoffice)}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions - Only show for multi-use and one-time passes */}
            {(passType === 'multi-use-pass' || passType === 'one-time-pass') && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <button 
                    onClick={() => setIsEditing(true)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit Pass
                  </button>
                  <button 
                    onClick={handleActivate}
                    disabled={saving || pass?.status === 'active'}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Activating...
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-4 w-4 mr-2" />
                        Activate Pass
                      </>
                    )}
                  </button>
                  <button 
                    onClick={handleDeactivate}
                    disabled={saving || pass?.status === 'inactive'}
                    className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Deactivating...
                      </>
                    ) : (
                      <>
                        <XCircleIcon className="h-4 w-4 mr-2" />
                        Deactivate Pass
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Status Summary */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Pass Status</span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(pass.status)}`}>
                    {pass.status || 'Unknown'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Deal Status</span>
                  <span className="text-sm font-medium text-gray-900">{pass.dealstatus || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Gift Status</span>
                  <span className="text-sm font-medium text-gray-900">{pass.dealgiftsent ? 'Sent' : 'Not Sent'}</span>
                </div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Pass Amount</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(pass.passamount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Discounted Price</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(pass.dealDiscountedPrice)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Payment Method</span>
                  <span className="text-sm font-medium text-gray-900">{pass.paymentby || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Deal Provider</label>
                  <p className="mt-1 text-sm text-gray-900">{pass.dealProviderName || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Deal Rating</label>
                  <p className="mt-1 text-sm text-gray-900">{pass.dealsrating || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Duration</label>
                  <p className="mt-1 text-sm text-gray-900">{pass.duration} {pass.durationUnit}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiUsePassDetail; 